/**
 * TIER A — FILL A PRACTICE DOCUMENT ON SCREEN — `tasks/practice-documents.md` §1.1, §5, Phase 5.
 *
 * A full-screen `ngx-extended-pdf-viewer` over the blank the practice sent, with AcroForm editing
 * on, that hands the FILLED bytes back to the invite wizard. The wizard then posts them through
 * the SAME return endpoint tier C uses — `POST /patient-invites/:id/documents/:requestId/files` —
 * because a filled PDF is just a PDF, and a second return path would be a second thing to keep in
 * step with the claim, the media rows and the review screen.
 *
 * ── WHY THIS LIVES UNDER THE WIZARD'S OWN FOLDER ─────────────────────────────────────────────
 *
 * It has exactly one host. `practice-document-settings` is the house precedent for that shape —
 * its upload and edit dialogs are nested directories inside the screen that opens them — and
 * `lessons.md` §2's warning applies in the other direction too: a component put in `shared/`
 * against a single caller is generalised against guesswork. It also means the existing
 * `verify:forms-invite-wizard` glob
 * (`src/app/user/components/patient-invite-wizard/**\/*.spec.ts`) already covers this spec, so
 * the dialog and the step that opens it are proven by one run rather than two.
 *
 * ── MOBILE FIRST. THIS IS A PHONE IN A WAITING ROOM. ─────────────────────────────────────────
 *
 * That is §5's own framing and it drives the layout rather than decorating it:
 *
 *   · The dialog is opened at 100vw × 100dvh by the wizard. There is no room on a phone for a
 *     dialog inside a page, and a form field that opens the keyboard inside a 400px-tall scroll
 *     box is a form field nobody can see while they type.
 *   · `mobileFriendlyZoom` is on and a phone opens at full size (`openingZoom`), so a field is
 *     legible and tappable without a pinch. A patient who has to zoom before they can read a label will not finish.
 *   · The action bar is a STICKY BOTTOM bar with full-width targets, outside the scrolling
 *     viewer, so Send is reachable without scrolling ten pages back to the top — and it sits
 *     above the safe-area inset so an iPhone's home indicator does not cover it.
 *   · The toolbar is stripped to zoom and paging. Print, download, open-file, find, presentation
 *     mode and the sidebar are all off: on a 375px-wide toolbar they push the controls that
 *     matter off the edge, and every one of them is a way out of the flow rather than through it.
 *
 * ── THE FALLBACK IS THE POINT, NOT AN ERROR PATH ─────────────────────────────────────────────
 *
 * §3.2 decision 4 records that `hasAcroForm`'s zero default "reads as tier C — the path that
 * works for every document". This component honours that AT RUNTIME: any document that claims
 * `embedded` and then cannot actually be filled — the bytes will not load, the viewer will not
 * start, the PDF turns out to have no widget annotations after all — closes itself with
 * `status: 'unavailable'` and a reason, and the wizard puts the patient back on the download
 * path. It never leaves somebody staring at a viewer they cannot complete.
 *
 * The three reasons are kept apart deliberately (`lessons.md` §14: "no results" and "could not
 * read the results" are different facts). `no-fields` is a practice configuration problem;
 * `load-failed` is a transport problem — a dead SAS, an offline phone, or a storage account with
 * no CORS rule for this origin; `export-failed` means the patient's typing exists and we could
 * not get it out, which is the only one of the three where something is at risk.
 */

import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, Inject, NgZone, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxExtendedPdfViewerService, pdfDefaultOptions } from 'ngx-extended-pdf-viewer';

/** What the wizard hands in. `blankUrl` is the short-lived SAS its own step already minted. */
export interface DocumentFillDialogData {
  /** The practice's own name for the document, shown in the bar. */
  documentName: string;
  /** The short-lived read URL for the blank. Minted by `POST …/documents/:requestId/blank`. */
  blankUrl: string;
  /** What the blank is called, used to name the file that goes back. */
  fileName: string | null;
}

/** Why an `embedded` document could not be filled on screen. */
export type DocumentFillUnavailableReason = 'no-fields' | 'load-failed' | 'export-failed';

/**
 * What `afterClosed()` carries.
 *
 * `undefined` means the patient backed out and nothing has changed — they are returned to the
 * step exactly as it was, viewer still offered.
 */
export type DocumentFillDialogResult =
  | { status: 'filled'; file: File }
  | { status: 'unavailable'; reason: DocumentFillUnavailableReason };

/**
 * The largest page image pdf.js should draw on this device, or null for no change.
 *
 * pdf.js caps phones and tablets at 5,242,880 pixels because Safari runs out of canvas memory
 * above that, but ngx-extended-pdf-viewer sets 16,777,216 for every device. On an iPhone a zoomed
 * page then fails to redraw and the old image stays stretched, blurry and out of line with the
 * boxes. Same detection as pdf.js: iPhone/iPad/iPod, Android, or a touch "Mac" (an iPad).
 */
export function canvasPixelLimit(userAgent: string, platform: string, maxTouchPoints: number): number | null {
  const isIOS = /\b(iPad|iPhone|iPod)(?=;)/.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1);
  const isAndroid = /Android/.test(userAgent);
  return isIOS || isAndroid ? 5242880 : null;
}

/**
 * The zoom the document opens at. Fitting a whole page to a phone leaves boxes a few pixels tall,
 * too small to tap, so a narrow screen opens at full size and the patient scrolls sideways. A
 * wide screen fits the page width, which is already about full size or more.
 */
export function openingZoom(viewportWidth: number): number | 'page-width' {
  return viewportWidth < 800 ? 100 : 'page-width';
}

@Component({
  selector: 'app-document-fill-dialog',
  templateUrl: './document-fill-dialog.component.html',
  styleUrls: ['./document-fill-dialog.component.scss'],
})
export class DocumentFillDialogComponent implements AfterViewInit, OnDestroy {

  /**
   * How long the viewer gets to say ANYTHING — loaded or failed — before this gives up.
   *
   * `pdfLoadingFailed` covers a rejected load, but it cannot cover a load that never settles: a
   * SAS URL fetched cross-origin from a storage account with no CORS rule for this origin can
   * leave pdf.js waiting, and a patient watching a spinner has no idea the download button two
   * taps away would have worked. The timeout is what turns "nothing happened" into the fallback.
   */
  readonly loadTimeoutMs = 25000;

  /** True once the blank is loaded AND has been proven to contain fillable fields. */
  ready = false;

  /** The Send is in flight — `getCurrentDocumentAsBlob()` re-serialises the whole document. */
  isExporting = false;

  /**
   * An export that failed, said on screen with the dialog STILL OPEN.
   *
   * Closing here would throw away everything the patient has typed with no way back, so the
   * choice is theirs: try again, or take the download path and lose only the typing.
   */
  exportError: string | null = null;

  /** How many fillable fields the blank turned out to have. Shown while `ready`. */
  fieldCount = 0;

  private loadTimer: ReturnType<typeof setTimeout> | null = null;
  private settled = false;

  readonly zoom = openingZoom(window.innerWidth);

  /** The page's viewport setting before this dialog opened, put back when it closes. */
  private viewportBefore: string | null = null;

  constructor(
    private dialogRef: MatDialogRef<DocumentFillDialogComponent, DocumentFillDialogResult>,
    private viewerService: NgxExtendedPdfViewerService,
    private zone: NgZone,
    @Inject(MAT_DIALOG_DATA) public data: DocumentFillDialogData,
    @Inject(DOCUMENT) private document: Document,
  ) {
    const limit = canvasPixelLimit(navigator.userAgent, navigator.platform, navigator.maxTouchPoints || 1);
    if (limit !== null) pdfDefaultOptions.maxCanvasPixels = limit;
  }

  ngAfterViewInit(): void {
    this.lockPageZoom();
    this.loadTimer = setTimeout(() => this.giveUp('load-failed'), this.loadTimeoutMs);
  }

  ngOnDestroy(): void {
    this.clearTimer();
    this.restorePageZoom();
  }

  /* ── The viewer's two outcomes ───────────────────────────────────────────────────────────── */

  /**
   * The bytes rendered. NOW ask whether there is anything to fill in.
   *
   * `hasAcroForm` was measured on the server when the blank was uploaded, and the practice cannot
   * set `fillMode: 'embedded'` without it — but that is a claim about the file the store
   * inspected, and this is the file the patient's browser actually has. `getFormData()` counts
   * the widget annotations pdf.js can see, which is the same thing the editor will offer, so it
   * is the only measurement that can answer "can this person complete it here".
   */
  async onPdfLoaded(): Promise<void> {
    if (this.settled) return;
    let fields: unknown[] = [];
    try {
      fields = await this.viewerService.getFormData(false);
    } catch (error) {
      this.giveUp('load-failed');
      return;
    }
    if (!Array.isArray(fields) || fields.length === 0) {
      this.giveUp('no-fields');
      return;
    }
    this.zone.run(() => {
      this.clearTimer();
      this.fieldCount = fields.length;
      this.ready = true;
    });
  }

  /** pdf.js could not read the document — a dead link, no network, a blocked cross-origin read. */
  onPdfLoadingFailed(): void {
    this.giveUp('load-failed');
  }

  /* ── Sending ─────────────────────────────────────────────────────────────────────────────── */

  /**
   * Export what the patient typed and hand it to the wizard.
   *
   * `getCurrentDocumentAsBlob()` writes the annotation storage back into the PDF, so the returned
   * bytes are the ORIGINAL document with values in its fields — still a PDF, still fillable, and
   * accepted by the return endpoint's own magic-byte sniff without a single change there.
   *
   * ZERO BYTES IS A FAILURE, NOT A SEND. `lessons.md` §18 is the same shape one layer over: an
   * artefact with a plausible name and no content is worse than a loud error, because the
   * practice files it and nobody finds out until somebody opens it.
   */
  async send(): Promise<void> {
    if (!this.ready || this.isExporting) return;
    this.isExporting = true;
    this.exportError = null;

    let blob: Blob | null = null;
    try {
      blob = await this.viewerService.getCurrentDocumentAsBlob();
    } catch (error) {
      blob = null;
    }

    if (!blob || !blob.size) {
      this.zone.run(() => {
        this.isExporting = false;
        this.exportError = 'We could not save what you filled in. Please try again — or use the '
          + 'download option below and send us a photo of each page instead.';
      });
      return;
    }

    const file = new File([blob], this.completedFileName(), { type: 'application/pdf' });
    this.settled = true;
    this.clearTimer();
    this.dialogRef.close({ status: 'filled', file });
  }

  /**
   * "I would rather download it" — from inside the viewer, at any point.
   *
   * It is `export-failed` rather than a plain cancel because the patient has ASKED for the other
   * path; a cancel leaves the viewer on offer, and this takes it away for the rest of the step.
   */
  useDownloadInstead(): void {
    this.giveUp('export-failed');
  }

  /** Back to the step with nothing changed. The viewer stays on offer. */
  cancel(): void {
    if (this.settled) return;
    this.settled = true;
    this.clearTimer();
    this.dialogRef.close(undefined);
  }

  /* ── Naming what goes back ───────────────────────────────────────────────────────────────── */

  /**
   * `SAQ.pdf` → `SAQ-completed.pdf`.
   *
   * A reviewer sees this name beside the blank's own in the patient file, and two files called
   * `SAQ.pdf` — the one that was sent and the one that came back — is the ambiguity that makes
   * somebody open both.
   */
  completedFileName(): string {
    const raw = String(this.data.fileName || 'document.pdf').trim() || 'document.pdf';
    const base = raw.replace(/\.pdf$/i, '').slice(0, 180) || 'document';
    return `${base}-completed.pdf`;
  }

  /* ── Internals ───────────────────────────────────────────────────────────────────────────── */

  /** One exit for every "this cannot be filled here" outcome, and it can only fire once. */
  private giveUp(reason: DocumentFillUnavailableReason): void {
    if (this.settled) return;
    this.settled = true;
    this.clearTimer();
    this.zone.run(() => this.dialogRef.close({ status: 'unavailable', reason }));
  }

  /**
   * The boxes in a practice PDF have text far below 16px, so iOS zooms the whole page the moment
   * one is tapped. The viewer takes two-finger gestures for its own zoom, so the page can never be
   * pinched back out, and the boxes end up off their lines. `maximum-scale=1` stops that page
   * zoom; zooming the document itself still works through the viewer.
   */
  private lockPageZoom(): void {
    const meta = this.viewportMeta();
    if (!meta) return;
    this.viewportBefore = meta.content;
    if (!/maximum-scale/.test(meta.content)) meta.content = `${meta.content}, maximum-scale=1`;
  }

  private restorePageZoom(): void {
    const meta = this.viewportMeta();
    if (meta && this.viewportBefore !== null) meta.content = this.viewportBefore;
    this.viewportBefore = null;
  }

  private viewportMeta(): HTMLMetaElement | null {
    return this.document.querySelector('meta[name="viewport"]');
  }

  private clearTimer(): void {
    if (this.loadTimer !== null) {
      clearTimeout(this.loadTimer);
      this.loadTimer = null;
    }
  }
}
